import { computed, ref, watch } from "vue";
import { useStore } from "vuex";
import type { CustomCostFrequency, CustomCostItem } from "@/dtos/CostMetrics.dto";

// Custom costs (salaries, commissions, etc.) are saved per user in the browser for now.
const STORAGE_KEY_PREFIX = "sb_custom_cost_items";

const AVG_DAYS_PER_MONTH = 365.25 / 12;

export const CUSTOM_COST_FREQUENCY_OPTIONS: { value: CustomCostFrequency; label: string }[] = [
  { value: "monthly", label: "Per month" },
  { value: "one_off", label: "One-off (this period)" },
  { value: "per_deal", label: "Per deal closed" },
];

function readItems(key: string): CustomCostItem[] {
  try {
    const raw = window.localStorage.getItem(key);
    const parsed = raw ? JSON.parse(raw) : [];
    return Array.isArray(parsed) ? parsed : [];
  } catch {
    return [];
  }
}

function writeItems(key: string, items: CustomCostItem[]) {
  try {
    window.localStorage.setItem(key, JSON.stringify(items));
  } catch {
    // Storage unavailable (private mode / quota) — keep in-memory only
  }
}

export function getMonthsInRange(startDate: string, endDate: string): number {
  const days = (new Date(endDate).getTime() - new Date(startDate).getTime()) / 86_400_000;
  return Math.max(0, days / AVG_DAYS_PER_MONTH);
}

export function getCustomCostTotal(
  items: CustomCostItem[],
  options: { months: number; dealsClosed: number }
): number {
  return items.reduce((sum, item) => {
    const amount = Number(item.amount) || 0;
    if (item.frequency === "monthly") return sum + amount * options.months;
    if (item.frequency === "per_deal") return sum + amount * options.dealsClosed;
    return sum + amount;
  }, 0);
}

export function useCustomCostItems() {
  const store = useStore();
  const storageKey = computed(() => {
    const account = store.getters.account?.data;
    return `${STORAGE_KEY_PREFIX}:${account?.organizationId ?? "org"}:${account?.id ?? "user"}`;
  });

  const items = ref<CustomCostItem[]>(readItems(storageKey.value));

  watch(storageKey, (key) => {
    items.value = readItems(key);
  });

  watch(items, (value) => writeItems(storageKey.value, value), { deep: true });

  function addItem() {
    items.value.push({
      id: `${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
      label: "",
      amount: 0,
      frequency: "monthly",
    });
  }

  function removeItem(id: string) {
    items.value = items.value.filter((item) => item.id !== id);
  }

  return { items, addItem, removeItem };
}
